import { v4 as uuidv4 } from 'uuid';

import {
  CohortBuilderDefinitionLegacy,
  CohortDslLegacy,
  DerivedEventFilterLegacy,
  DerivedEventLegacy,
  DerivedEventPayloadLegacy,
  DerivedEventTypeLegacy,
  DSLValueLegacy,
  EventEntityTypeLegacy,
  EventFilterPayloadLegacy,
  EventModelTypeLegacy,
  EventPositionTypePayloadLegacy,
  FilterLegacy,
  FilterPayloadLegacy,
  IndexDateAttributePayloadLegacy,
  IndexDateCalendarDatePayloadLegacy,
  IndexDateEventInstancePayloadLegacy,
  IndexDatePayloadLegacy,
  IndexDateTypeLegacy,
  IndexDateTypePayloadLegacy,
  IndexDateVariableLegacy,
  InputFieldLegacy,
  InputFieldPayloadLegacy,
  SimpleEventLegacy,
  SimpleEventPayloadLegacy,
  SimpleEventTypeLegacy,
  StudyArmResponseDataLegacy,
  StudyPeriodRestrictionsLegacy,
  StudyPeriodRestrictionsPayloadLegacy,
  SubjectVariableCriteriaSelectionTypeLegacy,
  SubjectVariableLegacy,
  SubjectVariablePayloadLegacy,
  SubjectVariableTypeLegacy,
  SubjectVariableTypePayloadLegacy,
} from '@verantos-dev/types-cohorts';
import { generateUIModelFromDSL } from '@verantos-dev/util-cohort-definition-dsl-to-ui';
import {
  transformDerivedEventsToDsl,
  transformIndexDateToDsl,
  transformSimpleEventsToDsl,
  transformStudyPeriodRestrictionsToDsl,
  transformSubjectVariablesToDsl,
} from '@verantos-dev/util-cohort-definition-ui-to-dsl';

export const transformIndexDateTypeToPayload = (
  uiIndexDateType: IndexDateTypeLegacy
): IndexDateTypePayloadLegacy => {
  switch (uiIndexDateType) {
    case IndexDateTypeLegacy.CALENDAR:
      return IndexDateTypePayloadLegacy.CALENDAR_DATE;

    case IndexDateTypeLegacy.EVENT:
      return IndexDateTypePayloadLegacy.EVENT_INSTANCE;
  }
};

export const transformIndexDateTypePayloadToIndexDateType = (
  indexDateTypePayload: IndexDateTypePayloadLegacy
): IndexDateTypeLegacy => {
  switch (indexDateTypePayload) {
    case IndexDateTypePayloadLegacy.CALENDAR_DATE:
      return IndexDateTypeLegacy.CALENDAR;

    case IndexDateTypePayloadLegacy.EVENT_INSTANCE:
      return IndexDateTypeLegacy.EVENT;
  }
};

export const translateIndexDateValueToPayload = (
  indexDateValue: string,
  type: IndexDateTypeLegacy,
  events: (SimpleEventLegacy | DerivedEventLegacy)[]
):
  | IndexDateCalendarDatePayloadLegacy
  | IndexDateEventInstancePayloadLegacy
  | IndexDateAttributePayloadLegacy => {
  switch (type) {
    case IndexDateTypeLegacy.CALENDAR: {
      const calendarIndexDateValue: IndexDateCalendarDatePayloadLegacy = {
        dateValue: indexDateValue,
      };
      return calendarIndexDateValue;
    }
    case IndexDateTypeLegacy.EVENT: {
      if (indexDateValue === 'birth_datetime') {
        const attributeIndexDateValue: IndexDateAttributePayloadLegacy = {
          attribute: indexDateValue,
        };
        return attributeIndexDateValue;
      }

      const referencedEventId = events.find(
        (event) => event.name === indexDateValue.split('"')[1]
      )?.id;
      if (referencedEventId === undefined) {
        throw new Error(
          `Cannot find event id for event name ${indexDateValue}. Events:\n  ${events
            .map((event) => `${event.id} - ${event.type} - ${event.name}`)
            .join('\n  ')}`
        );
      }
      const eventInstanceIndexDateValue: IndexDateEventInstancePayloadLegacy = {
        eventId: referencedEventId,
        eventName: indexDateValue,
      };
      return eventInstanceIndexDateValue;
    }
  }
};
export const transformStudyPeriodRestrictionsToPayload = (
  studyPeriodRestriction: StudyPeriodRestrictionsLegacy | undefined
): StudyPeriodRestrictionsPayloadLegacy | undefined => {
  if (!studyPeriodRestriction) {
    return undefined;
  }
  return {
    overallStudyPeriodRestriction:
      studyPeriodRestriction.overallStudyPeriodRestriction &&
      studyPeriodRestriction.overallStudyPeriodRestriction.isEnabled
        ? {
            startDate: studyPeriodRestriction.overallStudyPeriodRestriction.startDate,
            endDate: studyPeriodRestriction.overallStudyPeriodRestriction.endDate,
          }
        : undefined,
    baselinePeriodRestriction:
      studyPeriodRestriction.baselinePeriodRestriction &&
      studyPeriodRestriction.baselinePeriodRestriction.isEnabled
        ? {
            daysBeforeIndexDate:
              studyPeriodRestriction.baselinePeriodRestriction.daysBeforeIndexDate,
          }
        : undefined,
    followUpPeriodRestriction:
      studyPeriodRestriction.followUpPeriodRestriction &&
      studyPeriodRestriction.followUpPeriodRestriction.isEnabled
        ? {
            daysAfterIndexDate: studyPeriodRestriction.followUpPeriodRestriction.daysAfterIndexDate,
          }
        : undefined,
  };
};

export const transformIndexDateVariableToPayload = (
  uiIndexDateVariable: IndexDateVariableLegacy | undefined,
  events: (SimpleEventLegacy | DerivedEventLegacy)[]
): IndexDatePayloadLegacy | undefined => {
  if (uiIndexDateVariable === undefined) {
    return undefined;
  }

  return {
    indexDateId: uiIndexDateVariable.id,
    indexDateType: transformIndexDateTypeToPayload(uiIndexDateVariable.type),
    indexDateValue: translateIndexDateValueToPayload(
      uiIndexDateVariable.indexDateValue,
      uiIndexDateVariable.type,
      events
    ),
    indexDateRangeValue: uiIndexDateVariable.indexDateRangeValue,
  };
};

export const transformSubjectVariableTypeToSubjectVariableTypePayload = (
  subjectVariableType: SubjectVariableTypeLegacy | undefined
): SubjectVariableTypePayloadLegacy => {
  if (subjectVariableType === undefined) {
    throw new Error('Subject variable type is undefined');
  }

  switch (subjectVariableType) {
    case SubjectVariableTypeLegacy.COUNT:
      return SubjectVariableTypePayloadLegacy.COUNT;
    case SubjectVariableTypeLegacy.DEMOGRAPHICS:
      return SubjectVariableTypePayloadLegacy.DEMOGRAPHIC;
  }
};

export const transformSubjectsVariablesToPayload = (
  uiSubjectsVariables: SubjectVariableLegacy[] | undefined
): SubjectVariablePayloadLegacy[] => {
  return (
    uiSubjectsVariables?.map((subjectVariable) => ({
      subjectVariableId: subjectVariable.id,
      subjectVariableName: subjectVariable.name,
      subjectVariableType: transformSubjectVariableTypeToSubjectVariableTypePayload(
        subjectVariable.type
      ),
      conceptFilters: transformFiltersToPayload(subjectVariable.conceptFilters),
      numericFilters: transformFiltersToPayload(subjectVariable.numericFilters),
      countFilter: subjectVariable.countFilter
        ? transformFilterToPayload(subjectVariable.countFilter)
        : undefined,
      criteriaSelection: subjectVariable.criteriaSelection,
    })) ?? []
  );
};

export const transformSimpleEventTypeToEventEntityTypePayload = (
  simpleEventType: SimpleEventTypeLegacy | undefined
): EventEntityTypeLegacy => {
  switch (simpleEventType) {
    case SimpleEventTypeLegacy.CONDITION_OCCURRENCE:
      return EventEntityTypeLegacy.CONDITION_OCCURRENCE;
    case SimpleEventTypeLegacy.DRUG_EXPOSURE:
      return EventEntityTypeLegacy.DRUG_EXPOSURE;
    case SimpleEventTypeLegacy.PROCEDURE_OCCURRENCE:
      return EventEntityTypeLegacy.PROCEDURE_OCCURRENCE;
    case SimpleEventTypeLegacy.MEASUREMENT:
      return EventEntityTypeLegacy.MEASUREMENT;
    case SimpleEventTypeLegacy.OBSERVATION:
      return EventEntityTypeLegacy.OBSERVATION;
    case SimpleEventTypeLegacy.VISIT_OCCURRENCE:
      return EventEntityTypeLegacy.VISIT_OCCURRENCE;
    default:
      throw new Error(`Cannot submit without a simple event type`);
  }
};

export const transformSimpleEventsToPayload = (
  uiSimpleEvents: SimpleEventLegacy[] | undefined
): SimpleEventPayloadLegacy[] => {
  return (
    uiSimpleEvents?.map((simpleEvent) => ({
      eventId: simpleEvent.id,
      eventName: simpleEvent.name,
      eventEntityType: transformSimpleEventTypeToEventEntityTypePayload(simpleEvent.type),
      conceptFilters: transformFiltersToPayload(simpleEvent.conceptFilters),
      numericFilters: transformFiltersToPayload(simpleEvent.numericFilters),
      relatedEntityFilters: transformFiltersToPayload(simpleEvent.relatedEntityFilters),
      uniquenessFilters: transformFiltersToPayload(simpleEvent.uniquenessFilters),
    })) ?? []
  );
};

export const transformFilterToPayload = (filter: FilterLegacy): FilterPayloadLegacy => {
  return {
    filterId: filter.id,
    inputFields: transformInputFieldsToPayload(filter.inputFields),
  };
};

export const transformFiltersToPayload = (
  uiFilters: FilterLegacy[] | undefined
): FilterPayloadLegacy[] => {
  return uiFilters?.map((filter) => transformFilterToPayload(filter)) ?? [];
};

export const transformInputFieldsToPayload = (
  uiInputFields: InputFieldLegacy[] | undefined
): InputFieldPayloadLegacy[] => {
  return (
    uiInputFields?.map((inputField) => ({
      inputFieldId: inputField.id,
      inputFieldValue: inputField.value || '',
    })) ?? []
  );
};

export const transformDerivedEventTypeToEventModelTypePayload = (
  derivedEventType?: DerivedEventTypeLegacy
): EventModelTypeLegacy => {
  switch (derivedEventType) {
    case DerivedEventTypeLegacy.INSTANCE:
      return EventModelTypeLegacy.INSTANCE;
    case DerivedEventTypeLegacy.MERGED:
      return EventModelTypeLegacy.MERGED;
    case DerivedEventTypeLegacy.SEQUENCE:
      return EventModelTypeLegacy.SEQUENCE;
    default:
      throw new Error(`Cannot submit without a derived event type`);
  }
};

export const transformEventFiltersToEventsPayload = (
  uiEventFilters: DerivedEventFilterLegacy[] | undefined,
  events: (DerivedEventLegacy | SimpleEventLegacy)[]
): EventFilterPayloadLegacy[] => {
  return (
    uiEventFilters?.map((eventFilter) => {
      const referencedEventId = events.find(
        (event) => event.name === eventFilter.eventName.value?.split('"')[1]
      )?.id;

      if (referencedEventId === undefined) {
        throw new Error(
          `Cannot find event id for event name ${eventFilter.eventName.value}. Events:\n  ${events
            .map((event) => `${event.id} - ${event.type} - ${event.name}`)
            .join('\n  ')}`
        );
      }

      return {
        eventFilterId: eventFilter.id,
        referencedEventId,
        referencedEventName: eventFilter.eventName.value || '',
        dateAttribute: eventFilter.eventDateAttribute.value || '',
        selected:
          eventFilter.isSelected === undefined
            ? undefined
            : eventFilter.isSelected.value === 'true'
            ? true
            : false,
        position:
          eventFilter.position === undefined
            ? undefined
            : (eventFilter.position.value as EventPositionTypePayloadLegacy),
        index:
          eventFilter.index !== undefined && eventFilter.index.value !== undefined
            ? parseInt(eventFilter.index.value)
            : undefined,
      };
    }) ?? []
  );
};

export const transformDerivedEventsToPayload = (
  uiDerivedEvents: DerivedEventLegacy[] | undefined,
  events: (DerivedEventLegacy | SimpleEventLegacy)[]
): DerivedEventPayloadLegacy[] => {
  return (
    uiDerivedEvents?.map((derivedEvent) => ({
      eventId: derivedEvent.id,
      eventName: derivedEvent.name,
      eventModelType: transformDerivedEventTypeToEventModelTypePayload(derivedEvent.type) ?? '',
      eventFilters: transformEventFiltersToEventsPayload(derivedEvent.eventFilters, events),
      anyOrder: derivedEvent.anyOrder,
      temporalConstraint: transformFiltersToPayload(derivedEvent.temporalConstraints),
      uniquenessFilters: transformFiltersToPayload(derivedEvent.uniquenessFilters),
    })) ?? []
  );
};

// export const transformCohortDefinitionToSaveDataPayload = (
//   cohortDefinition: CohortBuilderDefinitionLegacy
// ): SaveStudyArmDataPayloadLegacy => {
//   const events = [...cohortDefinition.simpleEvents, ...cohortDefinition.derivedEvents];
//   return {
//     cohortDefinition: {
//       cohortDSL: cohortDefinition.fullCohortDsl,
//       studyPeriodRestrictions: transformStudyPeriodRestrictionsToPayload(
//         cohortDefinition.studyPeriodRestrictions
//       ),
//       indexDate: transformIndexDateVariableToPayload(cohortDefinition.indexDate, events),
//       subjectVariables: transformSubjectsVariablesToPayload(cohortDefinition.subjectVariables),
//       simpleEvents: transformSimpleEventsToPayload(cohortDefinition.simpleEvents),
//       derivedEvents: transformDerivedEventsToPayload(cohortDefinition.derivedEvents, events),
//     },
//   };
// };

export const transformIndexDateValuePayloadToIndexDateValue = (
  indexDateValue:
    | IndexDateCalendarDatePayloadLegacy
    | IndexDateEventInstancePayloadLegacy
    | IndexDateAttributePayloadLegacy,
  indexDateType: IndexDateTypeLegacy
): string => {
  switch (indexDateType) {
    case IndexDateTypeLegacy.CALENDAR: {
      return (indexDateValue as IndexDateCalendarDatePayloadLegacy).dateValue;
    }
    case IndexDateTypeLegacy.EVENT: {
      if ((indexDateValue as IndexDateAttributePayloadLegacy).attribute) {
        return (indexDateValue as IndexDateAttributePayloadLegacy).attribute;
      } else {
        return (indexDateValue as IndexDateEventInstancePayloadLegacy).eventName;
      }
    }
  }
};

export const transformIndexDatePayloadToIndexDateVariable = (
  indexDate: IndexDatePayloadLegacy | undefined
): IndexDateVariableLegacy | undefined => {
  if (!indexDate) {
    return undefined;
  }
  const indexDateType = transformIndexDateTypePayloadToIndexDateType(indexDate.indexDateType);
  return {
    id: indexDate.indexDateId,
    type: indexDateType,
    indexDateValue: transformIndexDateValuePayloadToIndexDateValue(
      indexDate.indexDateValue,
      indexDateType
    ),
    indexDateRangeValue: indexDate.indexDateRangeValue,
  };
};

export const transformInputFieldPayloadToInputField = (
  filters: InputFieldPayloadLegacy
): InputFieldLegacy => {
  return {
    id: filters.inputFieldId,
    value: filters.inputFieldValue,
  };
};

export const transformInputFieldsPayloadToInputFields = (
  inputFieldsPayload: InputFieldPayloadLegacy[]
): InputFieldLegacy[] => {
  return inputFieldsPayload.map((inputFieldPayload) =>
    transformInputFieldPayloadToInputField(inputFieldPayload)
  );
};

export const transformSubjectVariableTypePayloadToSubjectVariableType = (
  subjectVariableTypePayload: SubjectVariableTypePayloadLegacy
): SubjectVariableTypeLegacy => {
  switch (subjectVariableTypePayload) {
    case SubjectVariableTypePayloadLegacy.COUNT:
      return SubjectVariableTypeLegacy.COUNT;
    case SubjectVariableTypePayloadLegacy.DEMOGRAPHIC:
      return SubjectVariableTypeLegacy.DEMOGRAPHICS;
  }
};

export const transformFilterPayloadToFilter = (
  filterPayload: FilterPayloadLegacy
): FilterLegacy => {
  return {
    id: filterPayload.filterId,
    inputFields: transformInputFieldsPayloadToInputFields(filterPayload.inputFields),
  };
};

export const transformFilterPayloadsToFilters = (
  filterPayloads: FilterPayloadLegacy[]
): FilterLegacy[] => {
  return filterPayloads.map((filterPayload) => transformFilterPayloadToFilter(filterPayload));
};

export const transformSubjectVariablesPayloadToSubjectVariable = (
  subjectVariablesPayload: SubjectVariablePayloadLegacy[]
): SubjectVariableLegacy[] => {
  return subjectVariablesPayload.map((subjectVariablePayload) => ({
    id: subjectVariablePayload.subjectVariableId,
    name: subjectVariablePayload.subjectVariableName,
    type: subjectVariablePayload.subjectVariableType
      ? transformSubjectVariableTypePayloadToSubjectVariableType(
          subjectVariablePayload.subjectVariableType as SubjectVariableTypePayloadLegacy
        )
      : undefined,
    conceptFilters: transformFilterPayloadsToFilters(subjectVariablePayload.conceptFilters),
    numericFilters: transformFilterPayloadsToFilters(subjectVariablePayload.numericFilters),
    countFilter: subjectVariablePayload.countFilter
      ? transformFilterPayloadToFilter(subjectVariablePayload.countFilter)
      : undefined,
    criteriaSelection: subjectVariablePayload.criteriaSelection,
  }));
};

export const transformEventEntityTypePayloadToEventEntityType = (
  eventEntityType: EventEntityTypeLegacy
): SimpleEventTypeLegacy => {
  switch (eventEntityType) {
    case EventEntityTypeLegacy.CONDITION_OCCURRENCE:
      return SimpleEventTypeLegacy.CONDITION_OCCURRENCE;
    case EventEntityTypeLegacy.DRUG_EXPOSURE:
      return SimpleEventTypeLegacy.DRUG_EXPOSURE;
    case EventEntityTypeLegacy.PROCEDURE_OCCURRENCE:
      return SimpleEventTypeLegacy.PROCEDURE_OCCURRENCE;
    case EventEntityTypeLegacy.MEASUREMENT:
      return SimpleEventTypeLegacy.MEASUREMENT;
    case EventEntityTypeLegacy.OBSERVATION:
      return SimpleEventTypeLegacy.OBSERVATION;
    case EventEntityTypeLegacy.VISIT_OCCURRENCE:
      return SimpleEventTypeLegacy.VISIT_OCCURRENCE;
  }
};

export const transformSimpleEventsPayloadToSimpleEvent = (
  simpleEventsPayload: SimpleEventPayloadLegacy[]
): SimpleEventLegacy[] => {
  return simpleEventsPayload?.map((simpleEventPayload) => ({
    id: simpleEventPayload.eventId,
    name: simpleEventPayload.eventName,
    type: transformEventEntityTypePayloadToEventEntityType(simpleEventPayload.eventEntityType),
    conceptFilters: transformFilterPayloadsToFilters(simpleEventPayload.conceptFilters),
    numericFilters: transformFilterPayloadsToFilters(simpleEventPayload.numericFilters),
    relatedEntityFilters: transformFilterPayloadsToFilters(simpleEventPayload.relatedEntityFilters),
    uniquenessFilters: transformFilterPayloadsToFilters(simpleEventPayload.uniquenessFilters),
  }));
};

export const transformEventModelTypeToDerivedEventType = (
  eventModelType: EventModelTypeLegacy | undefined
): DerivedEventTypeLegacy | undefined => {
  switch (eventModelType) {
    case EventModelTypeLegacy.INSTANCE:
      return DerivedEventTypeLegacy.INSTANCE;
    case EventModelTypeLegacy.MERGED:
      return DerivedEventTypeLegacy.MERGED;
    case EventModelTypeLegacy.SEQUENCE:
      return DerivedEventTypeLegacy.SEQUENCE;
    default:
      return undefined;
  }
};

export const transformEventFiltersPayloadToEventFilters = (
  eventsFilterPayload: EventFilterPayloadLegacy[]
): DerivedEventFilterLegacy[] => {
  return eventsFilterPayload.map(
    ({
      eventFilterId,
      referencedEventName,
      dateAttribute,
      selected,
      position,
      index,
    }): DerivedEventFilterLegacy => ({
      id: eventFilterId,
      eventName: { id: uuidv4(), value: referencedEventName },
      eventDateAttribute: { id: uuidv4(), value: dateAttribute },
      isSelected: selected ? { id: uuidv4(), value: selected ? 'true' : 'false' } : undefined,
      position: position ? { id: uuidv4(), value: position } : undefined,
      index: index ? { id: uuidv4(), value: index.toString() } : undefined,
    })
  );
};

export const transformDerivedEventsPayloadToDerivedEvent = (
  derivedEventsPayload: DerivedEventPayloadLegacy[]
): DerivedEventLegacy[] => {
  return derivedEventsPayload?.map(
    ({
      eventId,
      eventName,
      eventModelType,
      anyOrder,
      eventFilters,
      temporalConstraint,
      uniquenessFilters,
    }) => ({
      id: eventId,
      name: eventName,
      type: transformEventModelTypeToDerivedEventType(eventModelType),
      anyOrder,
      eventFilters: transformEventFiltersPayloadToEventFilters(eventFilters),
      temporalConstraints:
        (temporalConstraint && transformFilterPayloadsToFilters(temporalConstraint)) ?? [],
      uniquenessFilters:
        (uniquenessFilters && transformFilterPayloadsToFilters(uniquenessFilters)) ?? [],
    })
  );
};

export const transformSubjectVariablesPayloadToCriteriaDSLs = (
  subjectVariablesPayload: SubjectVariablePayloadLegacy[]
) => {
  const inclusionCriteria: DSLValueLegacy[] = [];
  const exclusionCriteria: DSLValueLegacy[] = [];

  subjectVariablesPayload.forEach((subjectVariablePayload) => {
    subjectVariablePayload.criteriaSelection ===
      SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION &&
      inclusionCriteria.push({
        id: subjectVariablePayload.subjectVariableId,
        value: subjectVariablePayload.subjectVariableName,
      });

    subjectVariablePayload.criteriaSelection ===
      SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION &&
      exclusionCriteria.push({
        id: subjectVariablePayload.subjectVariableId,
        value: subjectVariablePayload.subjectVariableName,
      });
  });

  return {
    inclusionCriteria,
    exclusionCriteria,
  };
};

export const transformSubjectVariablesToCriteriaDSLs = (
  subjectVariables: SubjectVariableLegacy[]
) => {
  const inclusionCriteria: DSLValueLegacy[] = [];
  const exclusionCriteria: DSLValueLegacy[] = [];

  subjectVariables.forEach((subjectVariable) => {
    subjectVariable.criteriaSelection === SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION &&
      inclusionCriteria.push({
        id: subjectVariable.id,
        value: subjectVariable.name,
      });

    subjectVariable.criteriaSelection === SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION &&
      exclusionCriteria.push({
        id: subjectVariable.id,
        value: subjectVariable.name,
      });
  });

  return {
    inclusionCriteria,
    exclusionCriteria,
  };
};

export const transformStudyPeriodRestrictionsPayloadToStudyPeriodRestrictions = (
  studyPeriodRestrictions: StudyPeriodRestrictionsPayloadLegacy | undefined
): StudyPeriodRestrictionsLegacy | undefined => {
  if (!studyPeriodRestrictions) {
    return undefined;
  }

  const { overallStudyPeriodRestriction, baselinePeriodRestriction, followUpPeriodRestriction } =
    studyPeriodRestrictions;

  return {
    overallStudyPeriodRestriction:
      overallStudyPeriodRestriction &&
      overallStudyPeriodRestriction.startDate &&
      overallStudyPeriodRestriction.endDate
        ? {
            isEnabled: true,
            startDate: overallStudyPeriodRestriction.startDate,
            endDate: overallStudyPeriodRestriction.endDate,
          }
        : undefined,
    baselinePeriodRestriction:
      baselinePeriodRestriction && baselinePeriodRestriction.daysBeforeIndexDate !== undefined
        ? {
            isEnabled: true,
            daysBeforeIndexDate: baselinePeriodRestriction.daysBeforeIndexDate,
          }
        : undefined,
    followUpPeriodRestriction:
      followUpPeriodRestriction && followUpPeriodRestriction.daysAfterIndexDate !== undefined
        ? {
            isEnabled: true,
            daysAfterIndexDate: followUpPeriodRestriction.daysAfterIndexDate,
          }
        : undefined,
  };
};

export const transformCohortDSLStringToCohortBuilderDefinition = (
  dsl: string | undefined,
  dirty?: boolean // set to true to annotate input fields as dirty
): CohortBuilderDefinitionLegacy => {
  const cohortBuilderModelLegacy = generateUIModelFromDSL(dsl, dirty);
  const { inclusionCriteria, exclusionCriteria } = transformSubjectVariablesToCriteriaDSLs(
    cohortBuilderModelLegacy.subjectVariables
  );

  const cohortDsl: CohortDslLegacy = {
    inclusionCriteria,
    exclusionCriteria,
    studyPeriodRestrictions: transformStudyPeriodRestrictionsToDsl(
      cohortBuilderModelLegacy.studyPeriodRestrictions
    ),
    subjectVariables: transformSubjectVariablesToDsl(cohortBuilderModelLegacy.subjectVariables),
    indexDate: cohortBuilderModelLegacy.indexDate
      ? transformIndexDateToDsl(cohortBuilderModelLegacy.indexDate)
      : undefined,
    simpleEvents: transformSimpleEventsToDsl(cohortBuilderModelLegacy.simpleEvents),
    derivedEvents: transformDerivedEventsToDsl(cohortBuilderModelLegacy.derivedEvents),
  };

  return {
    fullCohortDsl: dsl || '',
    cohortDsl,
    ...cohortBuilderModelLegacy,
  };
};

export const transformStudyArmDataToCohortBuilderDefinition = (
  studyArmData: StudyArmResponseDataLegacy | undefined
): CohortBuilderDefinitionLegacy | undefined => {
  if (!studyArmData) {
    return undefined;
  }

  const { cohortDefinition } = studyArmData;
  const {
    cohortDSL: cohortDslData,
    indexDate,
    subjectVariables,
    simpleEvents,
    derivedEvents,
    studyPeriodRestrictions,
  } = cohortDefinition;

  const fullCohortDsl = JSON.stringify(cohortDslData);
  const transformedIndexDate = transformIndexDatePayloadToIndexDateVariable(indexDate);
  const transformedSubjectVariables =
    transformSubjectVariablesPayloadToSubjectVariable(subjectVariables);
  const transformedSimpleEvents = transformSimpleEventsPayloadToSimpleEvent(simpleEvents);
  const transformedDerivedEvents = transformDerivedEventsPayloadToDerivedEvent(derivedEvents);
  const { inclusionCriteria, exclusionCriteria } =
    transformSubjectVariablesPayloadToCriteriaDSLs(subjectVariables);
  const transformedStudyPeriodRestrictions =
    transformStudyPeriodRestrictionsPayloadToStudyPeriodRestrictions(studyPeriodRestrictions);

  const cohortDsl: CohortDslLegacy = {
    inclusionCriteria,
    exclusionCriteria,
    studyPeriodRestrictions: transformStudyPeriodRestrictionsToDsl(
      transformedStudyPeriodRestrictions
    ),
    subjectVariables: transformSubjectVariablesToDsl(transformedSubjectVariables),
    indexDate: transformedIndexDate ? transformIndexDateToDsl(transformedIndexDate) : undefined,
    simpleEvents: transformSimpleEventsToDsl(transformedSimpleEvents),
    derivedEvents: transformDerivedEventsToDsl(transformedDerivedEvents),
  };

  return {
    fullCohortDsl,
    cohortDsl,
    indexDate: transformedIndexDate,
    studyPeriodRestrictions: transformedStudyPeriodRestrictions,
    subjectVariables: transformedSubjectVariables,
    simpleEvents: transformedSimpleEvents,
    derivedEvents: transformedDerivedEvents,
  };
};
